import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isWithinLimit, WINCH_VALUE_STEP } from '../../helpers/formatWinchValue';
import { measurementFieldErrors } from '../../helpers/measurementFieldErrors';

export interface MeasurementLimits {
    min: number;
    max: number;
}

interface MeasurementDraft {
    distance: number | null;
    depth: number | null;
    minDepth: number | null;
    maxDepth: number | null;
}

export interface SavedMeasurement {
    distance: number;
    depth: number;
    minDepth: number;
    maxDepth: number;
}

function isCompleteDraft(draft: MeasurementDraft): draft is SavedMeasurement {
    return (
        draft.distance != null &&
        draft.depth != null &&
        draft.minDepth != null &&
        draft.maxDepth != null
    );
}

interface UseMeasurementDraftInput {
    saved: SavedMeasurement;
    scanEnabled: boolean;
    distanceLimits: MeasurementLimits;
    depthLimits: MeasurementLimits;
    onSave: (values: SavedMeasurement) => void;
}

export function useMeasurementDraft({
    saved,
    scanEnabled,
    distanceLimits,
    depthLimits,
    onSave,
}: UseMeasurementDraftInput) {
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState<MeasurementDraft>(saved);

    const shown: MeasurementDraft = isEditing ? draft : saved;
    const errors = measurementFieldErrors(shown, scanEnabled, distanceLimits, depthLimits, {
        distance: t('winchControl.form.distanceValidation', {
            min: distanceLimits.min,
            max: distanceLimits.max,
            step: WINCH_VALUE_STEP,
        }),
        depth: t('winchControl.form.depthValidation', {
            min: depthLimits.min,
            max: depthLimits.max,
            step: WINCH_VALUE_STEP,
        }),
        scanOrder: t('winchControl.form.minMaxOrder'),
    });
    const draftInRange =
        isCompleteDraft(draft) &&
        isWithinLimit(draft.distance, distanceLimits.min, distanceLimits.max) &&
        (scanEnabled
            ? isWithinLimit(draft.minDepth, depthLimits.min, depthLimits.max) &&
              isWithinLimit(draft.maxDepth, depthLimits.min, depthLimits.max) &&
              draft.minDepth < draft.maxDepth
            : isWithinLimit(draft.depth, depthLimits.min, depthLimits.max));

    const setField = (field: keyof MeasurementDraft, value: number | null): void => {
        setDraft((current) => ({ ...current, [field]: value }));
    };

    const startEdit = (): void => {
        setDraft(saved);
        setIsEditing(true);
    };

    const cancelEdit = (): void => {
        setDraft(saved);
        setIsEditing(false);
    };

    const saveEdit = (): void => {
        if (!draftInRange || !isCompleteDraft(draft)) {
            return;
        }
        onSave(draft);
        setIsEditing(false);
    };

    return {
        isEditing,
        shown,
        errors,
        draftInRange,
        setField,
        startEdit,
        cancelEdit,
        saveEdit,
    };
}
